import React, { useState, useEffect } from "react";

export default function SlidingPuzzle() {
  const [gameState, setGameState] = useState("menu");
  const [playerName, setPlayerName] = useState("");
  const [difficulty, setDifficulty] = useState(3); // 3x3, 4x4, 5x5
  const [tiles, setTiles] = useState([]);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [ranking, setRanking] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isGameStarted, setIsGameStarted] = useState(false);

  const PUZZLE_COLORS = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#FFA07A",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E2",
    "#F8B88B",
    "#85C1E2",
    "#F5A962",
    "#A8E6CF",
    "#FFD3B6",
    "#FFAAA5",
    "#FF8B94",
  ];

  // Timer
  useEffect(() => {
    if (gameState === "playing") {
      const timer = setInterval(() => setTime((t) => t + 1), 1000);
      return () => clearInterval(timer);
    }
  }, [gameState]);

  const playSound = (type) => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === "move") {
        oscillator.frequency.value = 500;
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.1
        );
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } else if (type === "win") {
        oscillator.frequency.value = 1000;
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.5
        );
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      }
    } catch (e) {
      console.log("Audio no disponible");
    }
  };

  const initializePuzzle = (diff) => {
    const size = diff * diff;
    const newTiles = Array.from({ length: size }, (_, i) => i);

    // Barajar
    for (let i = newTiles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newTiles[i], newTiles[j]] = [newTiles[j], newTiles[i]];
    }

    setTiles(newTiles);
    setMoves(0);
    setTime(0);
  };

  const startGame = (diff) => {
    setDifficulty(diff);
    initializePuzzle(diff);
    setGameState("playing");
    setIsGameStarted(true);
  };

  const getEmptyIndex = () => tiles.indexOf(tiles.length - 1);

  const canMove = (index) => {
    const emptyIndex = getEmptyIndex();
    const tileRow = Math.floor(index / difficulty);
    const tileCol = index % difficulty;
    const emptyRow = Math.floor(emptyIndex / difficulty);
    const emptyCol = emptyIndex % difficulty;

    return (
      (tileRow === emptyRow && Math.abs(tileCol - emptyCol) === 1) ||
      (tileCol === emptyCol && Math.abs(tileRow - emptyRow) === 1)
    );
  };

  const moveTile = (index) => {
    if (canMove(index)) {
      playSound("move");
      const emptyIndex = getEmptyIndex();
      const newTiles = [...tiles];
      [newTiles[index], newTiles[emptyIndex]] = [
        newTiles[emptyIndex],
        newTiles[index],
      ];
      setTiles(newTiles);
      setMoves(moves + 1);

      // Verificar victoria
      if (isSolved(newTiles)) {
        playSound("win");
        saveScore();
        setGameState("victory");
      }
    }
  };

  const isSolved = (tileArray) => {
    for (let i = 0; i < tileArray.length - 1; i++) {
      if (tileArray[i] !== i) return false;
    }
    return true;
  };

  const saveScore = () => {
    const newScore = {
      name: playerName || "Anónimo",
      difficulty: `${difficulty}x${difficulty}`,
      moves,
      time,
      score: Math.max(5000 - moves * 10 - time, 0),
    };
    const updated = [...ranking, newScore]
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
    setRanking(updated);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Menú principal
  if (gameState === "menu") {
    return (
      <div className="menu-container">
        <div className="menu-card">
          <h1 className="menu-title">🧩 Puzzle Deslizable</h1>
          <p className="menu-subtitle">Ordena las fichas en orden numérico</p>

          <div className="menu-form">
            <div className="form-group">
              <label>Tu nombre:</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Ingresa tu nombre"
                className="input-text"
              />
            </div>

            <div className="difficulty-section">
              <label>Selecciona dificultad:</label>
              <div className="difficulty-buttons">
                <button
                  onClick={() => startGame(3)}
                  className="difficulty-btn difficulty-easy"
                >
                  <div className="difficulty-size">3x3</div>
                  <div className="difficulty-label">Fácil</div>
                </button>
                <button
                  onClick={() => startGame(4)}
                  className="difficulty-btn difficulty-medium"
                >
                  <div className="difficulty-size">4x4</div>
                  <div className="difficulty-label">Medio</div>
                </button>
                <button
                  onClick={() => startGame(5)}
                  className="difficulty-btn difficulty-hard"
                >
                  <div className="difficulty-size">5x5</div>
                  <div className="difficulty-label">Difícil</div>
                </button>
              </div>
            </div>
          </div>

          {ranking.length > 0 && (
            <div className="ranking-section">
              <h2 className="ranking-title">🏆 Top 10 Ranking</h2>
              <div className="ranking-list">
                {ranking.map((score, idx) => (
                  <div key={idx} className="ranking-item">
                    <div className="ranking-left">
                      <span className="ranking-position">#{idx + 1}</span>
                      <span className="ranking-name">{score.name}</span>
                      <span className="ranking-difficulty">
                        {score.difficulty}
                      </span>
                    </div>
                    <div className="ranking-right">
                      <span className="ranking-moves">{score.moves} movs</span>
                      <span className="ranking-time">
                        {formatTime(score.time)}
                      </span>
                      <span className="ranking-score">{score.score} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pantalla de juego
  if (gameState === "playing" && isGameStarted) {
    const emptyIndex = getEmptyIndex();
    const size = difficulty * difficulty;
    const tileSize = Math.min(60, Math.floor(300 / difficulty));

    return (
      <div className="game-container">
        <div className="game-wrapper">
          {/* Header */}
          <div className="game-header">
            <h1 className="game-title">
              🧩 Puzzle {difficulty}x{difficulty}
            </h1>
            <div className="game-stats">
              <div className="stat-box">
                <span className="stat-label">⏱️</span>
                <span className="stat-value">{formatTime(time)}</span>
              </div>
              <div className="stat-box">
                <span className="stat-label">🎯</span>
                <span className="stat-value">{moves}</span>
              </div>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="btn-sound"
              >
                {soundEnabled ? "🔊" : "🔇"}
              </button>
            </div>
          </div>

          {/* Puzzle Grid */}
          <div className="puzzle-container">
            <div
              className="puzzle-grid"
              style={{
                gridTemplateColumns: `repeat(${difficulty}, 1fr)`,
                width: "max-content",
                gap: "8px",
              }}
            >
              {tiles.map((tile, index) => (
                <button
                  key={index}
                  onClick={() => moveTile(index)}
                  className={`puzzle-tile ${
                    index === emptyIndex ? "empty" : ""
                  }`}
                  style={{
                    width: tileSize,
                    height: tileSize,
                    backgroundColor:
                      index === emptyIndex
                        ? "transparent"
                        : PUZZLE_COLORS[tile % PUZZLE_COLORS.length],
                    fontSize: `${tileSize / 2.5}px`,
                  }}
                  disabled={index === emptyIndex}
                >
                  {index !== emptyIndex && tile + 1}
                </button>
              ))}
            </div>
          </div>

          {/* Instrucciones */}
          <div className="instructions">
            <p>Toca las fichas adyacentes al espacio vacío para moverlas</p>
          </div>
        </div>
      </div>
    );
  }

  // Pantalla de victoria
  if (gameState === "victory") {
    const finalScore = Math.max(5000 - moves * 10 - time, 0);
    return (
      <div className="victory-container">
        <div className="victory-card">
          <div className="victory-emoji">🎉</div>
          <h1 className="victory-title">¡Completaste!</h1>
          <p className="victory-subtitle">
            ¡Felicidades! Ordenaste el puzzle correctamente
          </p>

          <div className="victory-stats">
            <div className="stat-row">
              <span className="stat-label">Dificultad:</span>
              <span className="stat-value-large">
                {difficulty}x{difficulty}
              </span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Movimientos:</span>
              <span className="stat-value-large">{moves}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Tiempo:</span>
              <span className="stat-value-large">{formatTime(time)}</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-row">
              <span className="stat-label">Puntuación:</span>
              <span className="stat-value-score">{finalScore} pts</span>
            </div>
          </div>

          <div className="victory-buttons">
            <button onClick={() => setGameState("menu")} className="btn-menu">
              Volver al menú
            </button>
            <button onClick={() => startGame(difficulty)} className="btn-again">
              🔄 Jugar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }
}
